/**
 * Base → Quality (`/solutions/quality`): the ALCOA+ document scorecard.
 *
 * Pick documents in one collection; each is read in full and judged against
 * the nine ALCOA+ principles — Pass, Concern, Fail or Not assessable — with
 * quoted evidence, page citations and a suggested fix. Every concern or fail
 * carries a quote the server found in the document.
 *
 * Left: the caller's Quality runs. Right: a new run's form, or the selected
 * run's live status and, once finished, the documents × principles matrix, a
 * per-document drill-down and the PDF report.
 */

import { useCallback, useState } from "react";
import { Button, Card, Container, Grid, Group, Stack, Text, Title } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import {
  AdvisoryBanner,
  ReportDownloadButton,
  RunList,
  RunStatus,
} from "@/features/solutions/shared";
import type { RunDetailOut } from "@/shared/api/solutionRuns";
import { QualityDocumentDetail } from "./QualityDocumentDetail";
import { QualityForm } from "./QualityForm";
import { QualityMatrix } from "./QualityMatrix";
import { asQualityResult } from "./types";

function RunView({ runId }: { runId: string }) {
  const [run, setRun] = useState<RunDetailOut | null>(null);
  const [docId, setDocId] = useState<string | null>(null);
  const onUpdate = useCallback((r: RunDetailOut) => setRun(r), []);
  const result = run?.status === "succeeded" ? asQualityResult(run.result) : null;
  const doc =
    result?.documents.find((d) => d.document_id === docId) ?? result?.documents[0] ?? null;
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>{run?.name ?? "Run"}</Title>
        {run?.status === "succeeded" && <ReportDownloadButton run={run} />}
      </Group>
      <RunStatus runId={runId} onUpdate={onUpdate} />
      {result && (
        <>
          <AdvisoryBanner />
          <QualityMatrix
            result={result}
            selectedId={doc?.document_id ?? null}
            onSelect={setDocId}
          />
          <Text size="xs" c="dimmed">
            Original, Enduring and Available are usually Not assessable: they depend on the system
            that holds the record, not on the document&apos;s content.
          </Text>
          {doc && <QualityDocumentDetail key={doc.document_id} doc={doc} />}
        </>
      )}
    </Stack>
  );
}

export function QualityPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Container fluid py="lg">
      <Stack gap="md">
        <div>
          <Title order={2}>Quality</Title>
          <Text c="dimmed">
            An ALCOA+ scorecard for your documents, with quoted evidence and page citations.
          </Text>
        </div>
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 4, xl: 3 }}>
            <Card withBorder radius="md" p="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Your quality checks</Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setSelected(null)}
                  >
                    New check
                  </Button>
                </Group>
                <RunList
                  kind="quality"
                  selectedRunId={selected}
                  onSelect={(r) => setSelected(r.run_id)}
                  refreshKey={refreshKey}
                  emptyText="No quality checks yet."
                />
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 8, xl: 9 }}>
            <Card withBorder radius="md" p="md">
              {selected ? (
                <RunView key={selected} runId={selected} />
              ) : (
                <QualityForm
                  onCreated={(run) => {
                    setRefreshKey((k) => k + 1);
                    setSelected(run.run_id);
                  }}
                />
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
